import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { MoveTimeCriteriaType, ScopeObjectType } from '~/types';
import { PAGE_SIZE } from '~/modules/common/const';
import HierarchyOptionItem from '~/modules/common/components/HierarchyOptionItem';
import {
  SimpleAutocomplete,
  NoneDropdownItem
} from '~/modules/common/components/SearchAutocomplete';
import MoreResult from '~/modules/common/components/MoreResult';
import { MORE_AVAILABLE_OPTION_ID } from '~/modules/common/components/SearchAutocomplete/SearchAutocomplete';

import useDepartments from '~/modules/common/enhancers/useDepartments';
import useLocations from '~/modules/common/enhancers/useLocations';
import useEmployeeTypes from '~/modules/common/enhancers/useEmployeeTypes';
import useCostCenters from '~/modules/common/enhancers/useCostCenters';
import useServiceCenters from '~/modules/common/enhancers/useServiceCenters';
import useDivisions from '~/modules/common/enhancers/useDivisions';
import { usePageOfProjectResourceDropdownOptions } from '~/modules/tasks/components/ResourceUsersDropdown/hooks/usePageOfProjectResourceDropdownOptions';

const getOptionSelected = (option, value) => option.id === value.id;
const getOptionLabel = option => option?.displayText || '';

const CRITERIA_CONFIG = {
  [MoveTimeCriteriaType.Department]: {
    useDataHook: useDepartments,
    dataKey: 'departments',
    noOptionsTextId: 'departmentDropdown.noDepartment'
  },
  [MoveTimeCriteriaType.Location]: {
    useDataHook: useLocations,
    dataKey: 'locations',
    noOptionsTextId: 'locationsDropdown.noLocation'
  },
  [MoveTimeCriteriaType.EmployeeType]: {
    useDataHook: useEmployeeTypes,
    dataKey: 'employeeTypes',
    noOptionsTextId: 'employeeTypeDropdown.noEmployeeType'
  },
  [MoveTimeCriteriaType.CostCenter]: {
    useDataHook: useCostCenters,
    dataKey: 'costCenters',
    noOptionsTextId: 'costCenterDropdown.noCostCenter'
  },
  [MoveTimeCriteriaType.ServiceCenter]: {
    useDataHook: useServiceCenters,
    dataKey: 'serviceCenters',
    noOptionsTextId: 'serviceCenterDropdown.noServiceCenter'
  },
  [MoveTimeCriteriaType.Division]: {
    useDataHook: useDivisions,
    dataKey: 'divisions',
    noOptionsTextId: 'divisionDropdown.noDivision'
  },
  [MoveTimeCriteriaType.User]: {
    useDataHook: usePageOfProjectResourceDropdownOptions,
    dataKey: 'users',
    noOptionsTextId: 'taskResourceAssignments.noOptions',
    requiresProjectId: true,
    hasInfoMessage: true
  }
};

const useStyles = makeStyles(theme => ({
  hasInfoMessage: {
    fontStyle: 'italic',
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.primary
  }
}));

const CriteriaFacet = ({
  criterionType,
  value,
  onChange,
  label,
  disabled,
  projectId
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [searchTerm, setSearchTerm] = useState('');

  const selectedValues = useMemo(() => value || [], [value]);

  const config = useMemo(() => CRITERIA_CONFIG[criterionType], [criterionType]);

  const hookParams = useMemo(() => {
    if (criterionType === MoveTimeCriteriaType.User) {
      return {
        isAvailabilityEnabled: false,
        projectId,
        searchTerm,
        pageSize: PAGE_SIZE,
        includeAllocatedResource: false,
        includeAssignedToTask: false
      };
    }

    return {
      searchTerm,
      scope: ScopeObjectType.User
    };
  }, [criterionType, projectId, searchTerm]);

  const dataHookResult = config.useDataHook(hookParams);
  const { loading, hasMore } = dataHookResult;

  const options = useMemo(() => {
    if (criterionType === MoveTimeCriteriaType.User && config.hasInfoMessage) {
      const infoOption = {
        id: 'user-criteria-info',
        displayText: formatMessage({ id: 'moveTimeDialog.userCriteriaInfo' }),
        isInfo: true
      };

      const baseOptions = [infoOption];

      if (loading) {
        return [
          ...baseOptions,
          {
            id: 'loading-users',
            displayText: formatMessage({ id: 'moveTimeDialog.loading' }),
            isLoading: true,
            disabled: true
          }
        ];
      }

      const users = dataHookResult[config.dataKey] || [];

      if (users.length === 0) {
        return [
          ...baseOptions,
          {
            id: 'no-options',
            displayText: formatMessage({ id: config.noOptionsTextId }),
            isNoOptions: true,
            disabled: true
          }
        ];
      }

      return [...baseOptions, ...users];
    }

    return dataHookResult[config.dataKey] || [];
  }, [
    criterionType,
    config.hasInfoMessage,
    config.dataKey,
    config.noOptionsTextId,
    dataHookResult,
    formatMessage,
    loading
  ]);

  const renderOption = useCallback(
    option => {
      if (option.key === formatMessage({ id: 'NONE' })) {
        return <NoneDropdownItem value={option.displayText} />;
      }
      if (option.id === MORE_AVAILABLE_OPTION_ID) {
        return <MoreResult message={option.displayText} />;
      }

      if (option.isInfo) {
        return (
          <Typography className={classes.hasInfoMessage}>
            {option.displayText}
          </Typography>
        );
      }

      return (
        <HierarchyOptionItem hierarchyLevel={option.hierarchyLevel}>
          {option.displayText}
        </HierarchyOptionItem>
      );
    },
    [classes.hasInfoMessage, formatMessage]
  );

  const onInputChange = useCallback((_, val) => setSearchTerm(val), []);

  const filterOptions = useCallback(
    (allOptions, state) => {
      if (
        criterionType !== MoveTimeCriteriaType.User ||
        !config.hasInfoMessage
      ) {
        return allOptions;
      }

      const infoOption = allOptions.find(opt => opt.isInfo);
      const otherOptions = allOptions.filter(opt => !opt.isInfo);

      const filtered = otherOptions.filter(option => {
        if (option.isLoading || option.isNoOptions || !state.inputValue)
          return true;

        return option.displayText
          ?.toLowerCase()
          .includes(state.inputValue.toLowerCase());
      });

      if (state.inputValue && filtered.length === 0) {
        const noOptionsItem = {
          id: 'no-options',
          displayText: formatMessage({ id: config.noOptionsTextId }),
          isNoOptions: true,
          disabled: true
        };

        return infoOption ? [infoOption, noOptionsItem] : [noOptionsItem];
      }

      return infoOption ? [infoOption, ...filtered] : filtered;
    },
    [
      criterionType,
      config.hasInfoMessage,
      config.noOptionsTextId,
      formatMessage
    ]
  );

  const getOptionDisabled = useCallback(
    option =>
      selectedValues.some(v => v.id === option.id) ||
      option.id === MORE_AVAILABLE_OPTION_ID ||
      option.isInfo ||
      option.isLoading ||
      option.isNoOptions,
    [selectedValues]
  );

  return (
    <SimpleAutocomplete
      loading={loading}
      options={options}
      value={selectedValues}
      getOptionSelected={getOptionSelected}
      getOptionLabel={getOptionLabel}
      variant="filled"
      onChange={onChange}
      multiple
      fullWidth
      inputLabel={label}
      noOptionsText={formatMessage({ id: config.noOptionsTextId })}
      getOptionDisabled={getOptionDisabled}
      renderOption={renderOption}
      onInputChange={onInputChange}
      filterOptions={filterOptions}
      optionPropText="displayText"
      hasMore={hasMore}
      disabled={disabled}
    />
  );
};

CriteriaFacet.propTypes = {
  criterionType: PropTypes.oneOf(Object.keys(CRITERIA_CONFIG)).isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  projectId: PropTypes.string
};

export default CriteriaFacet;
